/**
 * Authentication Token Management Service
 * Handles different token formats for Laravel and Node.js backends
 */

import { getActiveBackend } from '../utils/backendConfig'

export interface TokenData {
  token: string
  laravel_token?: string
  user?: any
  expires_at?: string
}

/**
 * Get authentication headers based on active backend
 */
export const getAuthHeaders = (): Record<string, string> => {
  const backend = getActiveBackend()
  const tokenData = getStoredToken()
  
  if (!tokenData) {
    return {}
  }
  
  const headers: Record<string, string> = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
  
  if (backend.type === 'node') {
    // Node.js backend uses 'auth-token' header
    headers['auth-token'] = tokenData.token || tokenData.laravel_token || ''
  } else {
    // Laravel backend uses 'Authorization' header with Bearer prefix
    const token = tokenData.laravel_token || tokenData.token || ''
    headers['Authorization'] = `Bearer ${token}`
  }
  
  return headers
}

/**
 * Get stored token data from localStorage
 */
export const getStoredToken = (): TokenData | null => {
  try {
    const tokenString = localStorage.getItem('kt-auth-react-v')
    if (!tokenString) {
      return null
    }
    
    const tokenData = JSON.parse(tokenString) as TokenData
    return tokenData
  } catch (error) {
    console.error('Error parsing stored token:', error)
    return null
  }
}

/**
 * Store token data in localStorage
 */
export const setStoredToken = (tokenData: TokenData): void => {
  try {
    localStorage.setItem('kt-auth-react-v', JSON.stringify(tokenData))
  } catch (error) {
    console.error('Error storing token:', error)
  }
}

/**
 * Clear all stored authentication data
 */
export const clearStoredAuth = (): void => {
  localStorage.removeItem('kt-auth-react-v')
  localStorage.clear()
  sessionStorage.clear()
  
  console.log('🔐 All authentication data cleared')
}

/**
 * Clear authentication data due to backend switch
 */
export const clearAuthOnBackendSwitch = (oldBackend: string, newBackend: string): void => {
  localStorage.removeItem('kt-auth-react-v')
  localStorage.clear()
  sessionStorage.clear()
  
  console.log(`🔄 Backend switched from ${oldBackend} to ${newBackend} - Authentication data cleared`)
}

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const tokenData = getStoredToken()
  return tokenData !== null && (tokenData.token || tokenData.laravel_token) !== undefined
}

/**
 * Get authentication token for API requests
 */
export const getAuthToken = (): string | null => {
  const tokenData = getStoredToken()
  if (!tokenData) {
    return null
  }
  
  const backend = getActiveBackend()
  
  if (backend.type === 'node') {
    return tokenData.token || tokenData.laravel_token || null
  } else {
    return tokenData.laravel_token || tokenData.token || null
  }
}

/**
 * Validate token format based on backend type
 */
export const validateTokenFormat = (token: string): boolean => {
  if (!token) {
    return false
  }
  
  const backend = getActiveBackend()
  
  if (backend.type === 'node') {
    // Node.js tokens are typically UUIDs or simple strings
    return token.length > 10
  } else {
    // Laravel tokens are typically longer and may contain special characters
    return token.length > 10
  }
}

/**
 * Force logout and clear all authentication data
 */
export const forceLogout = (): void => {
  clearStoredAuth()
  
  // Redirect to login page
  if (window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

/**
 * Debug function to log current auth state
 */
export const debugAuthState = (): void => {
  const backend = getActiveBackend()
  const tokenData = getStoredToken()
  const headers = getAuthHeaders()
  
  console.log('🔐 Auth State Debug:', {
    backend: backend.type,
    hasToken: !!tokenData,
    tokenKeys: tokenData ? Object.keys(tokenData) : [],
    headers: Object.keys(headers),
    isAuthenticated: isAuthenticated()
  })
}
